import type { SxProps, Theme } from "@mui/material/styles";

import { isDarkMode } from "@/src/theme/helpers";
import { schemeSx } from "@/src/theme/schemeStyles";

/**
 * The wizard tab strip sits directly on top of the wizard panel, so the
 * selected tab has to use the panel surface and drop its bottom border to read
 * as one continuous shape. Inactive tabs stay recessed on the page surface and
 * keep their bottom border, which is what draws the rule across the panel top.
 */
const selectedSurface = (muiTheme: Theme): string =>
  muiTheme.palette.background.paper;

const inactiveSurface = (muiTheme: Theme): string =>
  isDarkMode(muiTheme)
    ? muiTheme.palette.background.default
    : muiTheme.palette.semantic.secondary.selected;

export const buildWizardCardSx = (isSelected: boolean): SxProps<Theme> =>
  schemeSx((muiTheme) => {
    const line = muiTheme.palette.misc.outlineBorder;

    return {
      backgroundColor: isSelected
        ? selectedSurface(muiTheme)
        : inactiveSurface(muiTheme),
      transition: "background-color 0.3s, box-shadow 0.3s",
      width: "100%",
      borderRadius: "1.25rem 1.25rem 0rem 0rem",
      // Every edge keeps a 1px border in both states so the two tab variants
      // stay exactly the same height and their bottom edges line up with the
      // separators. Only the colors swap.
      border: "1px solid",
      borderColor: isSelected ? line : "transparent",
      borderBottomColor: isSelected ? "transparent" : line,
      ...(isSelected
        ? {}
        : {
            "&:hover": {
              backgroundColor: muiTheme.palette.action.hover
            }
          }),
      p: 0.5,
      py: 0
    };
  });

/** Rule between two wizard tabs, continuing the inactive tab bottom border. */
export const wizardTabSeparatorSx: SxProps<Theme> = schemeSx((muiTheme) => ({
  borderBottom: `1px solid ${muiTheme.palette.misc.outlineBorder}`,
  width: "2rem",
  flexShrink: 0
}));
